import React, { useState, useEffect, useMemo, useRef } from "react";
import { useGetList } from "ra-core";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Trash2,
  Sparkles,
  Plus,
  Search,
  ChevronDown,
  X,
  Lock,
  AlertCircle,
  Check,
} from "lucide-react";
import type { ActionConfig, ActionType, TriggerItem } from "./types";
import {
  ACTION_TYPE_LABELS,
  ACTION_ENTITY_FIELDS,
  type ActionEntityFieldDef,
} from "./constants";
import { VariablePicker } from "./VariablePicker";
import { useDealCustomFields } from "../deals/useDealCustomFields";
import { usePipelines } from "../deals/usePipelines";
import type { Sale } from "../types";

interface ActionSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  triggerItem?: TriggerItem;
  config: ActionConfig;
  onSave: (config: ActionConfig) => void;
}

const normalizeSearch = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

function parseActionType(tipo: ActionType): {
  operacao: "criar" | "atualizar" | "excluir" | "criar_nota";
  entidade: TriggerItem | null;
} {
  if (tipo === "criar_nota") {
    return { operacao: "criar_nota", entidade: null };
  }
  const [op, ent] = tipo.split("_") as [
    "criar" | "atualizar" | "excluir",
    TriggerItem,
  ];
  return { operacao: op, entidade: ent || null };
}

export const ActionSheet: React.FC<ActionSheetProps> = ({
  open,
  onOpenChange,
  triggerItem,
  config: initialConfig,
  onSave,
}) => {
  const { fields: dealCustomFields } = useDealCustomFields();
  const { pipelines } = usePipelines();
  const { data: salesList = [] } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "first_name", order: "ASC" },
  });

  const salesOptions = useMemo(
    () => [
      {
        value: "{{item.sales_id}}",
        label: "🔄 Manter mesmo responsável do gatilho",
      },
      ...salesList.map((s) => ({
        value: String(s.id),
        label: `${s.first_name} ${s.last_name || ""}`.trim(),
      })),
    ],
    [salesList],
  );

  const [tipoAcao, setTipoAcao] = useState<ActionType>(
    initialConfig?.tipo_acao || "criar_atividade",
  );
  const [campos, setCampos] = useState<Record<string, any>>(
    initialConfig?.campos || {},
  );
  const [selectedFieldKeys, setSelectedFieldKeys] = useState<string[]>([]);
  const [tipoNota, setTipoNota] = useState<"deal" | "contact">(
    initialConfig?.tipo_nota || "deal",
  );
  const [notaText, setNotaText] = useState<string>(
    initialConfig?.nota_text || "",
  );
  const [activeField, setActiveField] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Estado do menu suspenso pesquisável de campos
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [fieldSearch, setFieldSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { operacao, entidade } = useMemo(
    () => parseActionType(tipoAcao),
    [tipoAcao],
  );

  // Lista completa de campos disponíveis para a entidade selecionada (incluindo campos personalizados de negócio)
  const entityFields: ActionEntityFieldDef[] = useMemo(() => {
    if (!entidade) return [];
    const baseFields = ACTION_ENTITY_FIELDS[entidade] || [];

    if (entidade === "negocio" && dealCustomFields?.length > 0) {
      const customDefs: ActionEntityFieldDef[] = dealCustomFields.map((cf) => ({
        key: cf.id.startsWith("cf_") ? cf.id : `cf_${cf.id}`,
        label: `${cf.name} (Personalizado)`,
        requiredOnCreate: Boolean(cf.requiredRule?.requiredOnCreate),
        inputType:
          (cf.type === "select" || cf.type === "autocomplete") &&
          cf.options &&
          cf.options.length > 0
            ? "select"
            : cf.type === "user"
              ? "sales_select"
              : cf.type === "number" || cf.type === "currency"
                ? "number"
                : cf.type === "textarea"
                  ? "textarea"
                  : cf.type === "date"
                    ? "date"
                    : "text",
        options:
          cf.options?.map((opt) => ({ value: opt, label: opt })) || undefined,
        placeholder: `Valor para ${cf.name}...`,
        isCustomField: true,
      }));

      // Evita duplicar chaves já presentes
      const existingKeys = new Set(baseFields.map((f) => f.key));
      return [
        ...baseFields,
        ...customDefs.filter((cf) => !existingKeys.has(cf.key)),
      ];
    }

    return baseFields;
  }, [entidade, dealCustomFields]);

  // Etapas disponíveis (de todos os funis, já identificando a qual funil cada etapa pertence)
  const availableStages = useMemo(() => {
    if (!pipelines || pipelines.length === 0) return [];
    const seen = new Set<string>();
    const list: { value: string; label: string; funilId: string | number }[] =
      [];
    for (const pipe of pipelines) {
      for (const st of pipe.etapas || []) {
        if (!seen.has(st.value)) {
          seen.add(st.value);
          list.push({
            value: st.value,
            label:
              pipelines.length > 1 ? `${st.label} (${pipe.nome})` : st.label,
            funilId: pipe.id,
          });
        }
      }
    }
    return list;
  }, [pipelines]);

  // Campos obrigatórios quando a operação é 'criar'
  const requiredFieldKeys = useMemo(() => {
    if (operacao !== "criar") return new Set<string>();
    return new Set(
      entityFields.filter((f) => f.requiredOnCreate).map((f) => f.key),
    );
  }, [operacao, entityFields]);

  // Inicializa estado ao abrir o Sheet
  useEffect(() => {
    if (!open) return;
    setValidationError(null);
    setDropdownOpen(false);
    setFieldSearch("");

    const initialType = initialConfig?.tipo_acao || "criar_atividade";
    setTipoAcao(initialType);
    setTipoNota(initialConfig?.tipo_nota || "deal");
    setNotaText(initialConfig?.nota_text || "");

    const { operacao: initOp, entidade: initEnt } =
      parseActionType(initialType);
    const savedCampos = { ...(initialConfig?.campos || {}) };
    const savedKeys = Object.keys(savedCampos).filter(
      (k) => savedCampos[k] !== undefined && k !== "funil_id",
    );

    if (initEnt) {
      const fieldsForEnt = ACTION_ENTITY_FIELDS[initEnt] || [];
      const reqKeys =
        initOp === "criar"
          ? fieldsForEnt.filter((f) => f.requiredOnCreate).map((f) => f.key)
          : [];

      // Preenche valores padrão para campos obrigatórios na criação caso ainda não existam
      if (initOp === "criar") {
        for (const f of fieldsForEnt) {
          if (
            f.requiredOnCreate &&
            f.defaultValueOnCreate !== undefined &&
            (savedCampos[f.key] === undefined || savedCampos[f.key] === "")
          ) {
            if (f.key === "stage" && availableStages.length > 0) {
              savedCampos[f.key] = availableStages[0].value;
            } else {
              savedCampos[f.key] = f.defaultValueOnCreate;
            }
          }
        }
      }

      const combinedKeys = Array.from(new Set([...reqKeys, ...savedKeys]));
      setSelectedFieldKeys(combinedKeys);
      setCampos(savedCampos);
      setActiveField(combinedKeys[0] || null);
    } else {
      setSelectedFieldKeys([]);
      setCampos(savedCampos);
    }
  }, [initialConfig, open, availableStages]);

  // Quando o usuário troca o Tipo de Ação no Select
  const handleActionTypeChange = (newType: ActionType) => {
    setTipoAcao(newType);
    setValidationError(null);
    setDropdownOpen(false);
    setFieldSearch("");

    const { operacao: nextOp, entidade: nextEnt } = parseActionType(newType);
    if (!nextEnt || nextOp === "excluir") {
      setSelectedFieldKeys([]);
      setCampos({});
      return;
    }

    const baseFields = ACTION_ENTITY_FIELDS[nextEnt] || [];
    if (nextOp === "criar") {
      const reqFields = baseFields.filter((f) => f.requiredOnCreate);
      const nextCampos: Record<string, any> = {};
      for (const f of reqFields) {
        if (f.key === "stage" && availableStages.length > 0) {
          nextCampos[f.key] = availableStages[0].value;
        } else {
          nextCampos[f.key] =
            f.defaultValueOnCreate !== undefined ? f.defaultValueOnCreate : "";
        }
      }
      const reqKeys = reqFields.map((f) => f.key);
      setSelectedFieldKeys(reqKeys);
      setCampos(nextCampos);
      setActiveField(reqKeys[0] || null);
    } else {
      // Em 'atualizar', começa sem campos forçados e abre o seletor para o usuário escolher
      setSelectedFieldKeys([]);
      setCampos({});
      setActiveField(null);
      setDropdownOpen(true);
    }
  };

  // Fecha o dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpen]);

  const handleFieldChange = (key: string, value: any) => {
    setValidationError(null);
    setCampos((prev) => ({ ...prev, [key]: value }));
  };

  const handleAddField = (fieldDef: ActionEntityFieldDef) => {
    if (!selectedFieldKeys.includes(fieldDef.key)) {
      setSelectedFieldKeys((prev) => [...prev, fieldDef.key]);
      setCampos((prev) => ({
        ...prev,
        [fieldDef.key]:
          prev[fieldDef.key] !== undefined
            ? prev[fieldDef.key]
            : fieldDef.inputType === "stage_select" &&
                availableStages.length > 0
              ? availableStages[0].value
              : fieldDef.inputType === "sales_select" && salesOptions.length > 0
                ? salesOptions[0].value
                : (fieldDef.defaultValueOnCreate ??
                  fieldDef.options?.[0]?.value ??
                  ""),
      }));
    }
    setActiveField(fieldDef.key);
    setDropdownOpen(false);
    setFieldSearch("");
  };

  const handleRemoveField = (key: string) => {
    if (requiredFieldKeys.has(key)) return; // Campos obrigatórios na criação não podem ser removidos
    setSelectedFieldKeys((prev) => prev.filter((k) => k !== key));
    setCampos((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    if (activeField === key) {
      setActiveField(selectedFieldKeys.find((k) => k !== key) || null);
    }
  };

  const handleInsertTag = (tag: string) => {
    if (tipoAcao === "criar_nota") {
      setNotaText((prev) => (prev ? `${prev} ${tag}` : tag));
      return;
    }
    const targetKey = activeField || selectedFieldKeys[0];
    if (targetKey) {
      const fieldDef = entityFields.find((f) => f.key === targetKey);
      if (
        fieldDef &&
        (fieldDef.inputType === "select" ||
          fieldDef.inputType === "stage_select" ||
          fieldDef.inputType === "sales_select" ||
          fieldDef.inputType === "number" ||
          fieldDef.inputType === "date")
      ) {
        handleFieldChange(targetKey, tag);
        return;
      }
      const current = campos[targetKey] ?? "";
      handleFieldChange(
        targetKey,
        current ? `${String(current)} ${tag}`.trim() : tag,
      );
    }
  };

  // Campos que estão ativos no formulário (mantendo obrigatórios no topo quando 'criar')
  const activeFieldDefs = useMemo(() => {
    const map = new Map(entityFields.map((f) => [f.key, f]));
    const allKeys = Array.from(
      new Set([...Array.from(requiredFieldKeys), ...selectedFieldKeys]),
    );

    return allKeys.map((key) => {
      const found = map.get(key);
      if (found) return found;
      return {
        key,
        label: key,
        inputType: "text" as const,
        placeholder: `Valor para ${key}...`,
      };
    });
  }, [entityFields, requiredFieldKeys, selectedFieldKeys]);

  // Campos disponíveis para adicionar no menu suspenso pesquisável
  const filteredAvailableFields = useMemo(() => {
    const q = normalizeSearch(fieldSearch.trim());
    return entityFields.filter((f) => {
      if (!q) return true;
      return (
        normalizeSearch(f.label).includes(q) ||
        normalizeSearch(f.key).includes(q)
      );
    });
  }, [entityFields, fieldSearch]);

  const handleSave = () => {
    setValidationError(null);

    if (operacao === "criar" && entidade) {
      for (const f of activeFieldDefs) {
        if (requiredFieldKeys.has(f.key)) {
          const val = campos[f.key];
          if (val === undefined || val === null || String(val).trim() === "") {
            setValidationError(
              `O campo obrigatório "${f.label}" precisa ser preenchido para criar o registro.`,
            );
            setActiveField(f.key);
            return;
          }
        }
      }
    }

    if (operacao === "atualizar" && entidade) {
      const filledKeys = activeFieldDefs.filter((f) => {
        const val = campos[f.key];
        return val !== undefined && val !== null && String(val).trim() !== "";
      });
      if (filledKeys.length === 0) {
        setValidationError(
          "Selecione e preencha pelo menos um campo para atualizar.",
        );
        return;
      }
    }

    // Filtra apenas os campos selecionados/obrigatórios
    const cleanCampos: Record<string, any> = {};
    for (const f of activeFieldDefs) {
      if (campos[f.key] !== undefined) {
        cleanCampos[f.key] = campos[f.key];
      }
    }

    // Vincula automaticamente o funil_id correspondente à Etapa do Funil escolhida
    if (entidade === "negocio" && cleanCampos.stage) {
      const matchedStage = availableStages.find(
        (s) => s.value === String(cleanCampos.stage),
      );
      if (matchedStage?.funilId !== undefined) {
        cleanCampos.funil_id = matchedStage.funilId;
      }
    }

    onSave({
      tipo_acao: tipoAcao,
      campos: cleanCampos,
      tipo_nota: tipoNota,
      nota_text: notaText,
    });
    onOpenChange(false);
  };

  const renderFieldInput = (field: ActionEntityFieldDef) => {
    const val = campos[field.key] ?? "";
    const isFocused = activeField === field.key;

    if (field.inputType === "textarea") {
      return (
        <Textarea
          rows={3}
          value={String(val)}
          onFocus={() => setActiveField(field.key)}
          onChange={(e) => handleFieldChange(field.key, e.target.value)}
          placeholder={
            field.placeholder || `Digite ${field.label.toLowerCase()}...`
          }
          className={`text-xs resize-none ${isFocused ? "ring-1 ring-primary border-primary" : ""}`}
        />
      );
    }

    if (field.inputType === "select" && field.options) {
      const currentVal = String(val || field.options[0]?.value || "");
      const hasCustomVal =
        currentVal && !field.options.some((o) => o.value === currentVal);
      return (
        <Select
          value={currentVal}
          onValueChange={(v) => {
            setActiveField(field.key);
            handleFieldChange(field.key, v);
          }}
        >
          <SelectTrigger className="w-full h-9 text-xs bg-background overflow-hidden">
            <SelectValue placeholder={`Selecione ${field.label}`} />
          </SelectTrigger>
          <SelectContent>
            {hasCustomVal && (
              <SelectItem value={currentVal} className="text-xs font-mono">
                {currentVal}
              </SelectItem>
            )}
            {field.options.map((opt) => (
              <SelectItem key={opt.value} value={opt.value} className="text-xs">
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    if (field.inputType === "stage_select") {
      const currentVal = String(val || "");
      const isKnownStage = availableStages.some((s) => s.value === currentVal);
      return (
        <Select
          value={currentVal}
          onValueChange={(v) => {
            setActiveField(field.key);
            handleFieldChange(field.key, v);
          }}
        >
          <SelectTrigger className="w-full h-9 text-xs bg-background overflow-hidden">
            <SelectValue placeholder="Selecione a etapa do funil..." />
          </SelectTrigger>
          <SelectContent>
            {currentVal && !isKnownStage && (
              <SelectItem value={currentVal} className="text-xs font-mono">
                {currentVal}
              </SelectItem>
            )}
            {availableStages.map((st) => (
              <SelectItem key={st.value} value={st.value} className="text-xs">
                {st.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    if (field.inputType === "sales_select") {
      const currentVal = String(val || salesOptions[0]?.value || "");
      const isKnownSale = salesOptions.some((s) => s.value === currentVal);
      return (
        <Select
          value={currentVal}
          onValueChange={(v) => {
            setActiveField(field.key);
            handleFieldChange(field.key, v);
          }}
        >
          <SelectTrigger className="w-full h-9 text-xs bg-background overflow-hidden">
            <SelectValue placeholder="Selecione o responsável..." />
          </SelectTrigger>
          <SelectContent>
            {currentVal && !isKnownSale && (
              <SelectItem value={currentVal} className="text-xs font-mono">
                {currentVal}
              </SelectItem>
            )}
            {salesOptions.map((s) => (
              <SelectItem key={s.value} value={s.value} className="text-xs">
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    const isDynamicVar = String(val).includes("{{");
    const inputTypeAttr = isDynamicVar
      ? "text"
      : field.inputType === "date"
        ? "date"
        : field.inputType === "number"
          ? "number"
          : "text";

    return (
      <Input
        type={inputTypeAttr}
        step={inputTypeAttr === "number" ? "any" : undefined}
        value={String(val)}
        onFocus={() => setActiveField(field.key)}
        onChange={(e) => handleFieldChange(field.key, e.target.value)}
        placeholder={
          field.placeholder || `Valor ou variável para ${field.label}...`
        }
        className={`h-9 text-xs bg-background ${isFocused ? "ring-1 ring-primary border-primary" : ""}`}
      />
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl flex flex-col h-full overflow-y-auto gap-0">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-border/60">
          <div className="flex items-center gap-2 text-primary font-semibold text-xs tracking-wider uppercase">
            <Sparkles className="w-4 h-4 text-primary" />
            Ação Interna do CRM
          </div>
          <SheetTitle className="text-xl font-bold">Configurar Ação</SheetTitle>
          <SheetDescription className="text-sm text-muted-foreground">
            Escolha qual operação o CRM executará automaticamente e selecione os
            campos que deseja preencher ou atualizar.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 px-6 py-5 space-y-6">
          {/* 1. SELEÇÃO DO TIPO DE AÇÃO */}
          <div className="space-y-2">
            <Label className="text-xs font-semibold text-foreground uppercase tracking-wider">
              1. Tipo de Ação
            </Label>
            <Select
              value={tipoAcao}
              onValueChange={(val) => handleActionTypeChange(val as ActionType)}
            >
              <SelectTrigger className="w-full h-10 text-xs">
                <SelectValue placeholder="Selecione o tipo de ação" />
              </SelectTrigger>
              <SelectContent>
                <div className="p-1 text-[11px] font-semibold text-muted-foreground">
                  Negócios
                </div>
                <SelectItem value="criar_negocio">Criar Negócio</SelectItem>
                <SelectItem value="atualizar_negocio">
                  Atualizar Negócio
                </SelectItem>
                <SelectItem value="excluir_negocio">Excluir Negócio</SelectItem>

                <div className="p-1 text-[11px] font-semibold text-muted-foreground mt-1">
                  Pessoas & Contatos
                </div>
                <SelectItem value="criar_pessoa">Criar Pessoa</SelectItem>
                <SelectItem value="atualizar_pessoa">
                  Atualizar Pessoa
                </SelectItem>
                <SelectItem value="excluir_pessoa">Excluir Pessoa</SelectItem>

                <div className="p-1 text-[11px] font-semibold text-muted-foreground mt-1">
                  Organizações
                </div>
                <SelectItem value="criar_organizacao">
                  Criar Organização
                </SelectItem>
                <SelectItem value="atualizar_organizacao">
                  Atualizar Organização
                </SelectItem>
                <SelectItem value="excluir_organizacao">
                  Excluir Organização
                </SelectItem>

                <div className="p-1 text-[11px] font-semibold text-muted-foreground mt-1">
                  Atividades & Histórico
                </div>
                <SelectItem value="criar_atividade">
                  Criar Atividade (Tarefa / Ligação / Reunião)
                </SelectItem>
                <SelectItem value="atualizar_atividade">
                  Atualizar Atividade
                </SelectItem>
                <SelectItem value="excluir_atividade">
                  Excluir Atividade
                </SelectItem>
                <SelectItem value="criar_nota">
                  Inserir Anotação no Histórico
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {ACTION_TYPE_LABELS[tipoAcao]?.desc}
            </p>
          </div>

          {/* 2. BARRA DE TAGS DE VARIÁVEIS (exceto em exclusão) */}
          {operacao !== "excluir" && (
            <VariablePicker
              title="Inserir Variável Dinâmica"
              description="Clique em um campo abaixo e depois clique na variável para injetar o valor do gatilho:"
              triggerItem={triggerItem}
              onSelect={handleInsertTag}
            />
          )}

          {/* 3. SELETOR DE CAMPOS COM PESQUISA (PARA CRIAR OU ATUALIZAR ENTIDADES) */}
          {(operacao === "criar" || operacao === "atualizar") && entidade && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <Label className="text-xs font-semibold text-foreground uppercase tracking-wider">
                    2. Campos{" "}
                    {operacao === "criar" ? "para Criação" : "para Atualizar"}
                  </Label>
                  <p className="text-[11px] text-muted-foreground">
                    {operacao === "criar"
                      ? "Os campos obrigatórios já estão fixados. Adicione outros campos opcionais no menu abaixo."
                      : "Use o menu suspenso abaixo para pesquisar e escolher quais campos serão editados."}
                  </p>
                </div>

                {/* MENU SUSPENSO COM PESQUISA DE CAMPOS */}
                <div className="relative" ref={dropdownRef}>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setDropdownOpen((o) => !o)}
                    className="h-9 text-xs gap-1.5 border-primary/50 text-primary hover:bg-primary/10 font-semibold cursor-pointer"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    {operacao === "criar"
                      ? "Adicionar Campo"
                      : "Selecionar Campo para Editar"}
                    <ChevronDown className="w-3.5 h-3.5 opacity-70" />
                  </Button>

                  {dropdownOpen && (
                    <div className="absolute right-0 mt-1.5 w-80 sm:w-96 rounded-xl border border-border bg-popover text-popover-foreground shadow-xl z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-100">
                      {/* Input de Pesquisa dentro do Menu Suspenso */}
                      <div className="p-2.5 border-b border-border/70 bg-muted/30">
                        <div className="relative">
                          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                          <input
                            type="text"
                            autoFocus
                            value={fieldSearch}
                            onChange={(e) => setFieldSearch(e.target.value)}
                            placeholder="Pesquisar campo para selecionar..."
                            className="w-full h-8 pl-8 pr-7 text-xs rounded-lg border border-border/80 bg-background outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                          />
                          {fieldSearch && (
                            <button
                              type="button"
                              onClick={() => setFieldSearch("")}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground cursor-pointer"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Lista de Campos do Menu Suspenso */}
                      <div className="max-h-64 overflow-y-auto divide-y divide-border/40 p-1">
                        {filteredAvailableFields.length === 0 ? (
                          <div className="py-6 text-center text-xs text-muted-foreground">
                            Nenhum campo encontrado para &quot;{fieldSearch}
                            &quot;.
                          </div>
                        ) : (
                          filteredAvailableFields.map((f) => {
                            const isAlreadyAdded =
                              requiredFieldKeys.has(f.key) ||
                              selectedFieldKeys.includes(f.key);
                            const isReq = requiredFieldKeys.has(f.key);

                            return (
                              <button
                                key={f.key}
                                type="button"
                                onClick={() => {
                                  if (isAlreadyAdded && !isReq) {
                                    handleRemoveField(f.key);
                                  } else {
                                    handleAddField(f);
                                  }
                                }}
                                className={`w-full flex items-center justify-between px-3 py-2 text-left text-xs rounded-lg transition-colors cursor-pointer ${
                                  isAlreadyAdded
                                    ? "bg-primary/10 text-primary font-semibold"
                                    : "hover:bg-muted text-foreground"
                                }`}
                              >
                                <div className="flex flex-col min-w-0 pr-2">
                                  <span className="truncate">{f.label}</span>
                                  <span className="text-[10px] font-mono text-muted-foreground">
                                    {f.key}
                                  </span>
                                </div>

                                <div className="flex items-center gap-1.5 shrink-0">
                                  {isReq ? (
                                    <Badge
                                      variant="outline"
                                      className="text-[10px] bg-amber-500/10 text-amber-600 border-amber-500/30 gap-1"
                                    >
                                      <Lock className="w-2.5 h-2.5" />
                                      Obrigatório
                                    </Badge>
                                  ) : isAlreadyAdded ? (
                                    <Badge
                                      variant="outline"
                                      className="text-[10px] bg-primary/15 text-primary border-primary/30 gap-1"
                                    >
                                      <Check className="w-2.5 h-2.5" />
                                      Selecionado
                                    </Badge>
                                  ) : (
                                    <span className="text-[11px] text-muted-foreground">
                                      + Adicionar
                                    </span>
                                  )}
                                </div>
                              </button>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Alerta de erro de validação */}
              {validationError && (
                <div className="p-3 rounded-xl border border-rose-500/30 bg-rose-500/10 text-xs text-rose-600 dark:text-rose-400 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>{validationError}</span>
                </div>
              )}

              {/* Lista de Campos Ativos para Preenchimento */}
              {activeFieldDefs.length === 0 ? (
                <div className="p-6 text-center rounded-xl border-2 border-dashed border-border/70 bg-muted/15 space-y-2">
                  <p className="text-xs font-medium text-foreground">
                    Nenhum campo selecionado para atualização.
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    Clique em{" "}
                    <strong>&quot;Selecionar Campo para Editar&quot;</strong>{" "}
                    acima para escolher quais campos esta automação deve
                    alterar.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setDropdownOpen(true)}
                    className="h-8 text-xs gap-1.5 mt-1 cursor-pointer"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Escolher Campos
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {activeFieldDefs.map((field) => {
                    const isRequired = requiredFieldKeys.has(field.key);
                    const isFocused = activeField === field.key;

                    return (
                      <div
                        key={field.key}
                        onClick={() => setActiveField(field.key)}
                        className={`p-3.5 rounded-xl border transition-all space-y-2 ${
                          isFocused
                            ? "border-primary/70 bg-primary/5 shadow-xs"
                            : "border-border/80 bg-card/50 hover:border-border"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <Label className="text-xs font-semibold text-foreground cursor-pointer">
                              {field.label}
                            </Label>
                            {isRequired ? (
                              <Badge
                                variant="outline"
                                className="text-[10px] py-0 h-4 bg-amber-500/10 text-amber-600 border-amber-500/30 gap-1"
                              >
                                <Lock className="w-2.5 h-2.5" />
                                Obrigatório
                              </Badge>
                            ) : (
                              <Badge
                                variant="secondary"
                                className="text-[10px] py-0 h-4 font-normal text-muted-foreground"
                              >
                                {operacao === "criar" ? "Opcional" : "Editar"}
                              </Badge>
                            )}
                          </div>

                          {!isRequired && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveField(field.key);
                              }}
                              title="Remover este campo da ação"
                              className="h-6 w-6 p-0 text-muted-foreground hover:text-rose-600 cursor-pointer"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>

                        {renderFieldInput(field)}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* 4. ANOTAÇÃO NO HISTÓRICO */}
          {tipoAcao === "criar_nota" && (
            <div className="space-y-3.5 p-4 rounded-xl border border-border/80 bg-card/40">
              <div className="space-y-1">
                <Label className="text-xs">Vincular anotação a:</Label>
                <Select
                  value={tipoNota}
                  onValueChange={(val) =>
                    setTipoNota(val as "deal" | "contact")
                  }
                >
                  <SelectTrigger className="w-full h-9 text-xs">
                    <SelectValue placeholder="Vincular a" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="deal">Negócio associado</SelectItem>
                    <SelectItem value="contact">
                      Contato / Pessoa associada
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Conteúdo da Anotação</Label>
                <Textarea
                  rows={4}
                  value={notaText}
                  onChange={(e) => setNotaText(e.target.value)}
                  placeholder="Ex: Automação executada: lead avançou para a etapa {{item.stage}}."
                  className="text-xs resize-none"
                />
              </div>
            </div>
          )}

          {/* 5. EXCLUSÃO */}
          {operacao === "excluir" && (
            <div className="p-4 rounded-xl border border-rose-500/30 bg-rose-500/5 space-y-1.5 text-xs text-rose-600 dark:text-rose-400">
              <div className="font-semibold flex items-center gap-1.5">
                <Trash2 className="w-4 h-4" />
                Atenção: Ação Destrutiva
              </div>
              <p className="text-muted-foreground text-[11px]">
                Esta ação excluirá o registro correspondente do CRM. Conforme a
                regra RN-12, um snapshot dos dados será registrado na auditoria
                da execução.
              </p>
            </div>
          )}
        </div>

        <SheetFooter className="px-6 py-4 border-t border-border/60 flex flex-row items-center justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave} className="gap-1.5 shadow-sm">
            Salvar Ação
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
